<template>
    <div class="allOptions">

        <div class="profile-badge" @click="$router.push('/settings')">
            <span class="icon">📋</span> {{ activeProfileName }}
        </div>

        <div class="user-guide" :class="{ 'collapsed': !isGuideExpanded }">
            <h3 @click="isGuideExpanded = !isGuideExpanded" class="guide-header">
                <span>💡 用家指南</span>
                <ChevronDown class="toggle-icon" :class="{ 'is-expanded': isGuideExpanded }" />
            </h3>
            <transition name="expand">
                <div v-if="isGuideExpanded" class="guide-content">
                    <ul>
                        <li>● 用法簡單：選取你糊的牌型，然後按計番。</li>
                        <li>● 你可以自訂糊的番數。例如你習慣正花=2番，你可以在設定裡更改。</li>
                        <li>● 如果以下列表有未列的牌，請往設定裡自行增加或刪減。</li>
                        <li>● 部分解釋並未完善，敬請見諒！如有建議，歡迎PM <a href="https://www.instagram.com/siuheibo.app?stkn=Y3RsbDF4NWkyamd1"
                                target="_blank">@siuheibo.app</a> 或電郵至 <a
                                href="mailto:mahjong@siuheibo.com">mahjong@siuheibo.com</a></li>
                    </ul>
                </div>
            </transition>
        </div>
        <div class="group-container">
            <div v-for="(group, index) in groups" :key="group.name" :id="'group-' + index" class="group">
                <h2>{{ group.name }}</h2>
                <div v-if="group.name == '花、番子'"
                    :class="[screenSize.width < 700 ? 'card-container' : 'card-container-mobile']">
                    <OptionCardSpecial v-for="option in group.options" :key="option.name" :title="option.name"
                        :value="option.value" :desc="option.desc" :isSelected="option.isSelected"
                        :quantity="option.quantity" @click="toggleSelect(option)" @addQuantity="addQuantity(option)"
                        @minusQuantity="minusQuantity(option)" />
                </div>
                <div v-if="group.name !== '花、番子'"
                    :class="[screenSize.width < 700 ? 'card-container' : 'card-container-mobile']">
                    <OptionCard v-for="option in group.options" :key="option.name" :title="option.name"
                        :value="option.value" :desc="option.desc" :isSelected="option.isSelected" :image="option.image"
                        @click="toggleSelect(option)" />
                </div>
            </div>
        </div>

        <!-- Sidebar Navigation -->
        <div class="sidebar" :class="{ visible: isScrolling }">
            <div v-for="(group, index) in groups" :key="index" class="sidebar-item"
                :class="{ active: activeGroup === index }" @click="scrollToGroup(index)">
                {{ group.name }}
            </div>
        </div>

    </div>
    <ResultsWindow v-if="isResultsVisible" :score=this.score :selectedOptions=this.selectedOptions
        @close="closeResultsWindow" />
    <div class="button-container">
        <button class="reset-button" :class="{ active: anyItemSelected }" @click="resetScore" title="重設">
            <RestartIcon :size="24" />
        </button>
        <button class="calculate-button" @click="calculateScore">
            計番！
        </button>
        <button class="settings-button" @click="$router.push('/settings')" title="設定">
            <SettingIcon fillColor="#666" :size="24" />
        </button>
    </div>

</template>

<script>
import OptionCard from '../components/OptionCard.vue'
import OptionCardSpecial from '../components/OptionCardSpecial.vue'
import { state } from '../assets/scores.js'
import ResultsWindow from '../components/ResultsWindow.vue'
import SettingIcon from 'vue-material-design-icons/Cog.vue'
import RestartIcon from 'vue-material-design-icons/Restart.vue'
import ChevronDown from 'vue-material-design-icons/ChevronDown.vue'
import AdMobService from '../services/AdMobService'
import { getInterstitialEveryN } from '../services/AdConfig'
import { Capacitor } from '@capacitor/core'

const CALC_COUNT_KEY = 'mj_calc_count'

export default {
    components: { OptionCard, OptionCardSpecial, ResultsWindow, SettingIcon, RestartIcon, ChevronDown },
    data() {
        return {
            state,
            isWeb: Capacitor.getPlatform() === 'web',
            screenSize: {
                width: 0,
                height: 0,
            },
            score: null,
            selectedOptions: null,
            isResultsVisible: false,
            activeGroup: 0,
            observer: null,
            isScrolling: false,
            scrollTimer: null,
            isGuideExpanded: true,
        }
    },
    computed: {
        groups() {
            return state.groups;
        },
        activeProfileName() {
            return state.activeProfile.name;
        },
        anyItemSelected() {
            return this.groups.some(group =>
                group.options.some(option => option.isSelected)
            );
        },
        userAvatar() {
            return state.user?.user_metadata?.avatar_url || state.user?.user_metadata?.picture;
        },
        userInitials() {
            if (!state.user) return '';
            const email = state.user.email || '';
            return email.charAt(0).toUpperCase();
        },
        buttonBottom() {
            return 30;
        }
    },
    async mounted() {
        window.addEventListener('resize', this.updateScreenSize)
        this.updateScreenSize()

        // Initialize AdMob for mobile apps
        if (!this.isWeb) {
            await this.initializeAds();
        }

        // Intersection Observer for sidebar
        const options = {
            root: null,
            rootMargin: '0px',
            threshold: 0.3
        }

        this.observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    const index = Number(entry.target.id.replace('group-', ''));
                    this.activeGroup = index;
                }
            });
        }, options);

        this.$nextTick(() => {
            const groups = document.querySelectorAll('.group');
            groups.forEach(group => {
                this.observer.observe(group);
            });
        });

        window.addEventListener('scroll', this.handleScroll);
    },

    methods: {
        async initializeAds() {
            try {
                await AdMobService.initialize();
            } catch (e) {
                console.error(e);
            }
        },
        updateScreenSize() {
            this.screenSize.width = window.innerWidth
            this.screenSize.height = window.innerHeight
        },
        scrollToGroup(index) {
            const element = document.getElementById('group-' + index);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
                this.activeGroup = index;
            }
        },
        toggleSelect(option) {
            option.isSelected = !option.isSelected
        },
        calculateScore() {
            // handle score calculation
            this.score = this.groups.reduce((total, group) => {
                return total + group.options.reduce((groupTotal, option) => {
                    if (option.isSelected) {
                        return groupTotal + option.value * option.quantity;
                    }
                    return groupTotal;
                }, 0)
            }, 0)

            // List out all selected options
            this.selectedOptions = this.groups.reduce((total, group) => {
                return total.concat(group.options.filter(option => option.isSelected))
            }, [])

            this.incrementCalcCount();

            // Open pop up window
            this.openResultsWindow();
        },
        getCalcCount() {
            const raw = localStorage.getItem(CALC_COUNT_KEY);
            const n = Number(raw);
            return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
        },
        incrementCalcCount() {
            const next = this.getCalcCount() + 1;
            localStorage.setItem(CALC_COUNT_KEY, String(next));
            return next;
        },
        shouldShowInterstitial() {
            const count = this.getCalcCount();
            const everyN = getInterstitialEveryN();
            return count > 0 && count % everyN === 0;
        },
        // handle quantity count 
        addQuantity(option) {
            if (option.quantity < 8) {
                option.quantity++
            }
        },
        minusQuantity(option) {
            if (option.quantity > 1) {
                option.quantity--
            }
        },
        resetScore() {
            // Reset all to unselected
            this.groups.forEach(group => {
                group.options.forEach(option => {
                    option.isSelected = false;
                    option.quantity = 1;
                });
            });
            this.score = null;
        },
        async closeResultsWindow() {
            this.isResultsVisible = false;
            if (!this.isWeb && this.shouldShowInterstitial()) {
                await AdMobService.showInterstitialIfReady();
            }
        },
        openResultsWindow() {
            this.isResultsVisible = true
        },
        handleScroll() {
            this.isScrolling = true;
            if (this.scrollTimer) {
                clearTimeout(this.scrollTimer);
            }
            this.scrollTimer = setTimeout(() => {
                this.isScrolling = false;
            }, 800);
        },
    }
}
</script>

<style scoped>
.allOptions {
    background-color: #fcfcfc;
    min-height: 100vh;
    text-align: center;
    padding-top: 20px;
}

.group {
    margin-bottom: 40px;
}

.group h2 {
    margin-top: 10px;
    margin-bottom: 25px;
    font-size: 20px;
    color: #2d3748;
    position: relative;
    display: inline-block;
    padding-bottom: 8px;
}

.group h2::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 40px;
    height: 3px;
    background: #4CAF50;
    border-radius: 2px;
}

@media (min-width: 800px) {
    .user-guide {
        margin: 0 auto 30px;
        max-width: 650px;
    }
}

.profile-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #e8f5e9;
    color: #2e7d32;
    padding: 6px 16px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 20px;
    cursor: pointer;
    transition: all 0.2s;
    border: 1px solid #c8e6c9;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.profile-badge:hover {
    background: #c8e6c9;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.profile-badge .icon {
    font-size: 16px;
}

.user-guide {
    background-color: #f8f9fa;
    border-radius: 12px;
    padding: 18px 25px;
    margin: 0 15px 30px;
    max-width: calc(100% - 30px);
    text-align: left;
    border-left: 4px solid #4CAF50;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;
}

.user-guide.collapsed {
    padding-bottom: 18px;
}

.guide-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    margin: 0 !important;
    user-select: none;
}

.toggle-icon {
    transition: transform 0.3s ease;
    color: #4CAF50;
}

.toggle-icon.is-expanded {
    transform: rotate(180deg);
}

.guide-content {
    margin-top: 10px;
    overflow: hidden;
}

.expand-enter-active,
.expand-leave-active {
    transition: all 0.3s ease;
    max-height: 200px;
    opacity: 1;
}

.expand-enter-from,
.expand-leave-to {
    max-height: 0;
    opacity: 0;
    margin-top: 0;
}

@media (min-width: 800px) {
    .user-guide {
        margin: 0 auto 30px;
        max-width: 650px;
    }
}

.user-guide a {
    color: #4CAF50;
    text-decoration: none;
    font-weight: 500;
    transition: opacity 0.2s;
}

.user-guide a:hover {
    text-decoration: underline;
    opacity: 0.8;
}

.user-guide h3 {
    margin-top: 0;
    margin-bottom: 10px;
    font-size: 16px;
    color: #333;
}

.user-guide ul {
    list-style: none;
    padding-left: 0;
    margin: 0;
}

.user-guide li {
    font-size: 13px;
    color: #666;
    margin-bottom: 6px;
    line-height: 1.5;
}

.user-guide li:last-child {
    margin-bottom: 0;
}

.group-container {
    margin-left: 1%;
    margin-right: 1%;
    margin-bottom: 120px;
}

.card-container,
.card-container-mobile {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: center;
    width: 100%;
}

@media (min-width: 700px) {
    .card-container-mobile {
        justify-content: center;
        gap: 20px;
    }
}

/* Sidebar Styles */
.sidebar {
    position: fixed;
    right: 10px;
    top: 50%;
    transform: translateY(-50%) translateX(30px);
    background-color: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(10px);
    padding: 12px 6px;
    border-radius: 18px;
    box-shadow: -5px 0 25px rgba(0, 0, 0, 0.15);
    z-index: 2000;
    max-height: 75vh;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 80px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    opacity: 0;
    pointer-events: none;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidebar.visible {
    opacity: 1;
    transform: translateY(-50%) translateX(0);
    pointer-events: auto;
}

@media (min-width: 820px) {
    .sidebar {
        right: calc((100% - 820px) / 2 + 10px);
    }
}

.sidebar-item {
    padding: 8px 4px;
    cursor: pointer;
    font-size: 11px;
    color: #666;
    border-radius: 8px;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    text-align: center;
    line-height: 1.3;
    word-break: break-all;
}

.sidebar-item:hover {
    background-color: #f0f0f0;
    color: #333;
}

.sidebar-item.active {
    background-color: #4CAF50;
    color: white;
    font-weight: 600;
    box-shadow: 0 4px 10px rgba(76, 175, 80, 0.3);
    transform: scale(1.05);
}

/* Button Container Styles */
.button-container {
    position: fixed;
    bottom: calc(30px + env(safe-area-inset-bottom));
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 15px;
    background-color: white;
    padding: 10px 25px;
    border-radius: 50px;
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    width: max-content;
    max-width: 90%;
}

.calculate-button {
    background-color: #4CAF50;
    color: white;
    border: none;
    padding: 12px 35px;
    font-size: 18px;
    font-weight: bold;
    border-radius: 25px;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
    transition: all 0.2s;
}

.calculate-button:active {
    transform: scale(0.95);
    box-shadow: 0 2px 4px rgba(76, 175, 80, 0.2);
}

.reset-button {
    background-color: #f8f9fa;
    color: #888;
    border: 1px solid #eee;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    padding: 0;
    line-height: 0;
    transition: all 0.2s;
}

.reset-button :deep(span),
.reset-button :deep(svg) {
    display: block;
    margin: 0;
}

.reset-button.active {
    color: #e74c3c;
    border-color: #ffa39e;
    background-color: #fff1f0;
    box-shadow: 0 2px 8px rgba(231, 76, 60, 0.1);
}

.reset-button.active:hover {
    background-color: #e74c3c;
    color: white;
}

.settings-button {
    background-color: #f8f9fa;
    color: #666;
    border: 1px solid #eee;
    border-radius: 50%;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50px;
    height: 50px;
    padding: 0;
    line-height: 0;
    transition: all 0.2s;
}

.settings-button :deep(span),
.settings-button :deep(svg) {
    display: block;
    margin: 0;
}

.settings-button:hover {
    background-color: #ececec;
}

@media (max-width: 600px) {
    .group-container {
        margin-right: 1%;
    }

    .sidebar {
        right: 2px;
        width: 65px;
        border-radius: 12px 0 0 12px;
        padding: 5px 2px;
    }

    .sidebar-item {
        font-size: 10px;
        padding: 6px 2px;
    }

    .button-container {
        bottom: calc(20px + env(safe-area-inset-bottom));
        gap: 10px;
        padding: 8px 15px;
    }

    .calculate-button {
        padding: 10px 20px;
        font-size: 16px;
    }

    .settings-button {
        width: 42px;
        height: 42px;
    }

    .reset-button {
        width: 42px;
        height: 42px;
        padding: 0;
    }
}
</style>